// Streaming client for /admin/agent/turn/stream.
//
// We use fetch + ReadableStream rather than EventSource because EventSource
// only supports GET. The wire format is standard SSE:
//
//   event: <type>
//   data: <json single line>
//   <blank line>
//
// Each `data:` line is JSON. We don't emit multi-line data, so the parser
// stays simple.

import { adminTokenHeader, clearAdminToken, isInvalidAdminTokenError } from "../../../auth/adminTokenStore";
import { AgentMessage } from "../../../api/admin/types";

const BASE_URL = process.env.REACT_APP_BE_URL as string;

export type StreamEvent =
  | { type: "text_delta"; text: string }
  | { type: "tool_use"; id: string; name: string; input: Record<string, unknown> }
  | { type: "tool_result"; tool_use_id: string; content: string }
  | { type: "done" }
  | { type: "error"; message: string };

export interface StreamHandlers {
  onEvent: (event: StreamEvent) => void;
  signal?: AbortSignal;
}

export interface StreamArgs {
  sessionId: string;
  messages: AgentMessage[];
  // Optional persistent chat id. When set, the backend upserts the resulting
  // transcript into AdminAgentChats so the chat survives a reload.
  chatId?: string | null;
}

function parseFrame(raw: string): StreamEvent | null {
  // A frame is one or more lines terminated by a blank line. We need at least
  // an `event:` and a `data:` line. Anything we don't recognize is dropped.
  let eventName = "message";
  let dataLine: string | null = null;
  for (const line of raw.split("\n")) {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLine = line.slice(5).trim();
    }
  }
  if (dataLine == null) return null;
  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(dataLine);
  } catch {
    return null;
  }
  return { type: eventName, ...payload } as StreamEvent;
}

export async function streamAgentTurn(
  args: StreamArgs,
  handlers: StreamHandlers
): Promise<void> {
  const resp = await fetch(`${BASE_URL}/admin/agent/turn/stream`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...adminTokenHeader(),
      "Content-Type": "application/json",
      Accept: "text/event-stream",
    },
    body: JSON.stringify({
      session_id: args.sessionId,
      messages: args.messages,
      chat_id: args.chatId ?? undefined,
    }),
    signal: handlers.signal,
  });

  if (!resp.ok || !resp.body) {
    let detail = `HTTP ${resp.status}`;
    const bodyText = await resp.text().catch(() => "");
    if (bodyText) detail = bodyText;

    if (resp.status === 403 && bodyText) {
      try {
        const parsed = JSON.parse(bodyText) as { error?: unknown };
        if (isInvalidAdminTokenError(resp.status, parsed)) {
          clearAdminToken();
        }
      } catch {
        /* not JSON - nothing to check */
      }
    }

    handlers.onEvent({ type: "error", message: detail });
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Split on the SSE record separator. The trailing partial frame stays in
    // the buffer for the next chunk.
    let sepIdx;
    while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      const evt = parseFrame(frame);
      if (evt) handlers.onEvent(evt);
    }
  }

  // Flush any trailing frame (rare - typically the server ends with a blank line).
  if (buffer.trim()) {
    const evt = parseFrame(buffer);
    if (evt) handlers.onEvent(evt);
  }
}
